/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, Renderer2, inject } from '@angular/core';
import { UserOperationMember } from '@data-access/api/user-operation';
import { BrandingService } from '@feature/branding';
import { BehaviorSubject, Observable, filter, take } from 'rxjs';
import { FreshchatWidget } from './freshchat-widget.type';

@Injectable({
  providedIn: 'root',
})
export class FreshchatService {
  private readonly brandingService: BrandingService = inject(BrandingService);

  private member: UserOperationMember | null;

  private readonly initialisedSubject$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly initialised$: Observable<boolean> = this.initialisedSubject$.asObservable();

  /*
   * Getter for the widget api: https://developers.freshchat.com/web-sdk/v1/
   */
  get freshchatWidget(): FreshchatWidget {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (window as any).fcWidget;
  }

  enabled(): boolean {
    return this.brandingService.branding().freshchat;
  }

  setMember(member: UserOperationMember | null): void {
    this.initialised$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.member = member;
    });
  }

  open(): void {
    this.initialised$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.initFields();
      this.freshchatWidget.open();
    });
  }

  clearUser(): void {
    this.freshchatWidget?.user?.clear();
  }

  initFreshchatScripts(renderer: Renderer2, doc: Document): void {
    if (!this.brandingService.branding().freshchatToken) {
      console.error('Freshchat token is not defined in branding configuration');
      return;
    }

    // eslint-disable-next-line @typescript-eslint/typedef
    const scriptBody = renderer.createElement('script');
    scriptBody.type = 'text/javascript';
    scriptBody.innerHTML = `window.fcWidgetMessengerConfig = {
        config: {
          headerProperty: {
            hideChatButton: true,
          },
        }
      }`;
    renderer.appendChild(doc.body, scriptBody);

    // eslint-disable-next-line @typescript-eslint/typedef
    const script = renderer.createElement('script');
    script.type = 'text/javascript';
    script.src = `https://eu.fw-cdn.com/${this.brandingService.branding().freshchatToken}`;
    renderer.appendChild(doc.body, script);

    this.initialisedSubject$.next(true);
  }

  private initFields(): void {
    if (this.member) {
      this.freshchatWidget.setExternalId(this.member.id);
      this.freshchatWidget.user.setFirstName(this.member.kyc.firstName);
      this.freshchatWidget.user.setLastName(this.member.kyc.lastName);
      this.freshchatWidget.user.setEmail(this.member.kyc.email);
      this.freshchatWidget.user.setPhone(this.member.kyc.phoneNumber, '');
    }
  }
}
